import 'react-phone-input-2/lib/style.css';

import type { CallInfo } from '@compass/client/types/call';
import { FieldGroupRenderer } from '@components/forms/FieldGroupRenderer';
import { RadioCardGroup } from '@components/forms/RadioCardGroup';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@components/ui/accordion';
import { useAutoFill } from '@hooks/useAutoFill';
import {
  useCase,
  useCaseCreationSuccess,
  useCreateCase,
  useUpdateCaseInput,
  useUpdateCaseOutput,
} from '@hooks/useCases';
import {
  classifyReporterRole,
  deriveFieldBadge,
  matchEnumOption,
  type PmsReporterRole,
  usePmsPrefill,
} from '@hooks/usePmsPrefill';
import { useSchema } from '@hooks/useSchema';
import { useWorkflowsByProgram } from '@hooks/useWorkflows';
import { useStore } from '@store/index';
import { getMirrorUpdates } from '@utils/autoCalculate';
import { separateMetadataGroup } from '@utils/cases';
import { getDefaultValueForField, validateField } from '@utils/formSchema';
import { separateFieldTypes } from '@utils/nestedAccordion';
import { getProgramBrand, getReadableTextColor, type ProgramBrand } from '@utils/programBrand';
import { applyRequiredFieldsToGroups } from '@utils/schemaValidation';
import { shouldShowLinkConfirmation } from '@utils/workflow';
import { getWorkflowColorClasses } from '@utils/workflowColor';
import { Building2, Globe, Mail, PhoneForwarded, RotateCcw, SendHorizonal } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { Breadcrumb, BreadcrumbPortal, HeaderActionsPortal } from '@/components/Header';
import { LiveSessionPanel } from '@/components/LiveSessionPanel';
import { PatientLookupCard } from '@/components/pms/PatientLookupCard';
import { WhoIsCallingGate } from '@/components/pms/WhoIsCallingGate';
import { Button } from '@/components/ui/button';
import {
  AEPQC_PREPROCESSING_WORKFLOW_TYPE,
  getSubmissionMethodLabel,
  PQCWorkflowVariant,
} from '@/constants/dispositions';
import { useActiveCall } from '@/hooks/useActiveCall';
import { useLinkToCase } from '@/hooks/useLinkToCase';
import { useMyCallHistory } from '@/hooks/useMyCallHistory';
import type { FieldGroup } from '@/types/formSchema';
import { isFieldVisible } from '@/utils/fieldVisibility';

import type { DefaultCreateCasePageProps } from './DefaultCreateCasePage';

// Dynamic FormData type based on field IDs
type DynamicFormData = Record<string, any>;

// Poll for the target rather than guessing a single delay: the section holding it has just been
// told to open, and Radix keeps collapsed accordion content unmounted, so the element appears a
// render later. ~1s total is generous, while still giving up rather than spinning if the field
// never renders at all.
const FOCUS_RETRY_INTERVAL_MS = 50;
const FOCUS_MAX_ATTEMPTS = 20;

// Scrolling waits for the accordions to finish animating, not merely for the field to exist.
// `scrollIntoView` resolves its destination offset once, at call time, and a smooth scroll then
// animates toward that fixed offset — so scrolling mid-animation aims at a position the field has
// since moved away from. The accordions animate over 200ms (`accordion-down` / `accordion-up` in
// tailwind.config.js); this clears that with room to spare.
const SCROLL_DELAY_MS = 250;

// Identifies the newest focus run, so a second failed submit supersedes the first instead of the
// two retry chains racing each other for focus.
let focusRunId = 0;

/**
 * Move focus to the field a failed submit flagged, then scroll it into view.
 *
 * Focus lands as soon as the field exists, so it is typeable immediately; only the scroll waits
 * for the section that was just opened to finish animating.
 */
const focusFieldWhenRendered = (fieldId: string): void => {
  const runId = ++focusRunId;

  const attempt = (n: number): void => {
    if (runId !== focusRunId) return;

    // Every field type puts its id on something focusable. The `data-field-id` wrapper is the
    // scroll target instead, so the label comes into view with the input rather than the input
    // sitting centred with its label clipped above.
    const control = document.getElementById(fieldId);
    const wrapper = document.querySelector<HTMLElement>(`[data-field-id="${CSS.escape(fieldId)}"]`);
    const anchor = wrapper ?? control;

    if (!anchor) {
      if (n < FOCUS_MAX_ATTEMPTS) {
        window.setTimeout(() => attempt(n + 1), FOCUS_RETRY_INTERVAL_MS);
      }
      return;
    }

    // preventScroll suppresses the browser's own focus jump, which would otherwise scroll
    // mid-animation and land in the wrong place.
    (control ?? anchor).focus({ preventScroll: true });
    window.setTimeout(() => {
      if (runId === focusRunId) {
        anchor.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, SCROLL_DELAY_MS);
  };

  attempt(0);
};

/**
 * Field ids for `groups` in the order FieldGroupRenderer paints them: only the fields it
 * actually renders, and within each group every scalar before any array/object accordion.
 *
 * This has to mirror the renderer, or "first error" stops meaning "topmost error on screen" —
 * the schema interleaves scalar and nested fields and the renderer does not.
 */
const renderedFieldIds = (groups: FieldGroup[], data: DynamicFormData): string[] =>
  groups.flatMap(group => {
    const { scalarFields, nestedFields } = separateFieldTypes(
      group.fields.filter(field => isFieldVisible(field, data)),
    );
    return [...scalarFields, ...nestedFields].map(field => field.id);
  });

/**
 * Prominent "Manufacturer" card shown at the top of the create-case form (a peer to the
 * Case Type / Mandatory / Additional sections), so agents immediately see which manufacturer
 * the case is being created for.
 *
 * When the workflow maps to a known brand it shows a colored initials tile (brand color +
 * monogram) on a branded background; otherwise it falls back to a stable hash-derived accent +
 * a building icon. The tile/icon + name are always shown together so the cue stays accessible
 * (color is additive). The initials tile is a POC placeholder for a real logo.
 */
const ManufacturerSection: React.FC<{
  name: string;
  brand: ProgramBrand | null;
  fallbackColorClass: string;
  variant?: string;
}> = ({ name, brand, fallbackColorClass, variant }) => {
  // No manufacturer to show (e.g. no workflow selected yet) — hide the section entirely rather
  // than rendering a placeholder. In practice the workflow always has a name by this point.
  if (!name) {
    return null;
  }

  // Submission method (Email / Portal / Warm Transfer) with a per-mode icon.
  const submissionMethod = getSubmissionMethodLabel(variant);
  const SubmissionIcon =
    variant === PQCWorkflowVariant.PORTAL
      ? Globe
      : variant === PQCWorkflowVariant.WARM_TRANSFER
        ? PhoneForwarded
        : Mail;

  return (
    <section
      className="sticky top-0 z-10 rounded-xl border bg-card shadow-sm overflow-hidden"
      style={brand ? { borderColor: `${brand.colorHex}59` } : undefined}
    >
      <div
        className={`flex items-center gap-4 px-5 py-4 ${brand ? '' : fallbackColorClass}`}
        style={
          brand
            ? { background: `linear-gradient(90deg, ${brand.colorHex}1f, ${brand.colorHex}0a)` }
            : undefined
        }
      >
        {/* Colored initials tile (POC placeholder for a real logo) */}
        {brand ? (
          <div
            className="flex h-14 w-14 items-center justify-center rounded-lg font-bold shrink-0"
            style={{
              backgroundColor: brand.colorHex,
              color: getReadableTextColor(brand.colorHex),
              fontSize:
                brand.monogram.length >= 3
                  ? '0.9rem'
                  : brand.monogram.length === 2
                    ? '1.1rem'
                    : '1.35rem',
            }}
          >
            {brand.monogram}
          </div>
        ) : (
          <div className="flex h-14 w-14 items-center justify-center rounded-lg bg-muted shrink-0">
            <Building2 className="h-7 w-7 text-muted-foreground" />
          </div>
        )}
        <div className="min-w-0">
          <div
            className="text-[11px] font-semibold uppercase tracking-wider opacity-70"
            style={brand ? { color: brand.textHex } : undefined}
          >
            Manufacturer
          </div>
          <div
            className="text-xl font-bold leading-tight truncate"
            style={brand ? { color: brand.textHex } : undefined}
          >
            {name}
          </div>
        </div>
        {/* Submission method (Email / Portal / Warm Transfer), right-aligned within the band */}
        <div className="ml-auto text-right shrink-0">
          <div
            className="text-[11px] font-semibold uppercase tracking-wider opacity-70"
            style={brand ? { color: brand.textHex } : undefined}
          >
            Submission
          </div>
          <div
            className="flex items-center justify-end gap-1.5 text-sm font-bold leading-tight"
            style={brand ? { color: brand.textHex } : undefined}
          >
            <SubmissionIcon className="h-4 w-4" />
            <span>{submissionMethod}</span>
          </div>
        </div>
      </div>
    </section>
  );
};

/**
 * KnipperCreateCasePage - PQC workflow-specific create case page
 *
 * Features a two-column layout:
 * - Left: Live session sidebar with active call info and recent call history
 * - Right: Create case form (RadioCardGroup for metadata + Accordion for other groups)
 *
 * Integrates call-to-case linking: users can link an active call or a history call
 * to pre-fill the form with existing case data.
 */
export const KnipperCreateCasePage: React.FC<DefaultCreateCasePageProps> = ({
  progId: progIdProp,
  caseRecordId: caseRecordIdProp,
}) => {
  const { progId: progIdParam } = useParams<{ progId: string }>();
  const progId = progIdProp || progIdParam;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Drop the caseRecordId from the URL so the page re-renders a fresh intake form
  // (CreateCaseRouter keys the page on caseRecordId, so clearing it remounts clean).
  // workflow_id is preserved, keeping the same manufacturer/workflow selected.
  const handleCreateFreshCase = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('caseRecordId');
    setSearchParams(next, { replace: true });
  };

  const { getSelectedWorkflow, getSelectedProgram, getWorkflowsArray } = useStore();
  const selectedProgram = getSelectedProgram();

  // Fetch workflows for the current program
  const { isLoading: workflowsLoading } = useWorkflowsByProgram(
    selectedProgram?.id || progId?.replace('prog-', '') || '',
  );

  const selectedWorkflow = getSelectedWorkflow();

  // Brand identity (logo + brand color) for this workflow, with a stable hash-derived accent
  // color as the fallback when the workflow isn't in the brand registry.
  const workflowBrand = getProgramBrand(selectedWorkflow);
  const workflowColorClass = getWorkflowColorClasses(selectedWorkflow);

  // Call hooks
  const { activeCall, isLoading: activeCallLoading } = useActiveCall(true);
  const { calls: callHistory, isLoading: callHistoryLoading } = useMyCallHistory(true);

  // Case creation hooks
  const createCase = useCreateCase();
  const updateCaseInput = useUpdateCaseInput();
  const updateCaseOutput = useUpdateCaseOutput();
  const linkToCaseMutation = useLinkToCase();
  const [linkingCallId, setLinkingCallId] = useState<string | null>(null);
  const [linkedCaseRecordId, setLinkedCaseRecordId] = useState<string | null>(null);
  // Persists the linked case ID for submit (linkedCaseRecordId is cleared after pre-fill)
  const [submittableLinkedCaseId, setSubmittableLinkedCaseId] = useState<string | null>(null);

  // Fetch linked case record data for form pre-fill
  const { data: linkedCaseRecord, error: linkedCaseRecordError } = useCase(linkedCaseRecordId);

  // Guard (LA-7428): the URL may point at a case that has ALREADY been created —
  // i.e. promoted past the aepqc_preprocessing draft (link-to-case done). This
  // happens regardless of how the agent got here (Calls list, browser back,
  // direct URL, stale/cleared localStorage, another device). Presenting the
  // re-link submit would call link-to-case again and 400. Derived from the
  // case's live workflow_type (server truth), so we short-circuit to a
  // "case already created" state instead of a doomed intake form.
  const caseAlreadyCreated =
    !!caseRecordIdProp &&
    !!linkedCaseRecord?.workflow_type &&
    linkedCaseRecord.workflow_type !== AEPQC_PREPROCESSING_WORKFLOW_TYPE;

  // Auto-link when navigated from Calls page with caseRecordId in URL
  useEffect(() => {
    if (caseRecordIdProp && !linkedCaseRecordId && !submittableLinkedCaseId) {
      setLinkedCaseRecordId(caseRecordIdProp);
      setSubmittableLinkedCaseId(caseRecordIdProp);
    }
  }, [caseRecordIdProp]); // eslint-disable-line react-hooks/exhaustive-deps

  // Shared success handler
  const handleCaseCreationSuccess = useCaseCreationSuccess();

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Auto-fill hook
  const { isAutoFillEnabled, hasSavedData, autoFill, saveFormData } = useAutoFill();

  // Fetch schema (pass workflow_type so PQC uses local JSON schema)
  const { inputGroups, outputGroups, isLoading: schemaLoading } = useSchema(selectedWorkflow?.id);

  // PMS patient lookup + prefill (AQ-580). Gated per manufacturer workflow via
  // the pms_enabled feature flag; the flag is also enforced server-side, so
  // this only controls rendering.
  const pmsEnabled = !!selectedWorkflow?.features?.pms_enabled;
  const pms = usePmsPrefill({
    organizationId: selectedProgram?.organization_id,
    programId: selectedProgram?.id,
    workflowId: selectedWorkflow?.id,
    enabled: pmsEnabled,
  });

  // Form state (declared before inputGroupsWithRequired so it can be used in the memo)
  const [formData, setFormData] = useState<DynamicFormData>({});
  // Output fields captured at create (schema-driven via capture_at_create, e.g. warm-transfer
  // ack_id). Kept separate from formData so they post to output_data, not input_data.
  const [outputFormData, setOutputFormData] = useState<DynamicFormData>({});

  // Only output fields flagged capture_at_create are rendered on the create form; the flag lives
  // in the schema (nomos), so this stays variant-agnostic — email/portal output isn't touched.
  const captureAtCreateGroups = useMemo(
    () =>
      outputGroups
        .map(group => ({ ...group, fields: group.fields.filter(f => f.captureAtCreate) }))
        .filter(group => group.fields.length > 0),
    [outputGroups],
  );
  const hasCaptureAtCreateFields = captureAtCreateGroups.length > 0;

  // Apply required fields based on current form data (handles conditional requirements)
  const inputGroupsWithRequired = useMemo(
    () => applyRequiredFieldsToGroups(inputGroups, formData),
    [inputGroups, formData],
  );

  // Build form schema from parsed input groups
  const formSchema = useMemo(
    () => ({ groups: inputGroupsWithRequired }),
    [inputGroupsWithRequired],
  );

  // Separate metadata group from other groups
  const { metadataGroup, otherGroups } = separateMetadataGroup(formSchema.groups);

  // Split otherGroups into mandatory/optional based on live required flags.
  // Since otherGroups comes from inputGroupsWithRequired, required flags already reflect
  // the current formData (including case_type), so fields move accordions when case type changes.
  const { mandatoryGroups, optionalGroups } = useMemo(() => {
    const mandatory: typeof otherGroups = [];
    const optional: typeof otherGroups = [];
    otherGroups.forEach(group => {
      const mandatoryFields = group.fields.filter(f => f.validation?.required);
      const optionalFields = group.fields.filter(f => !f.validation?.required);
      if (mandatoryFields.length > 0) mandatory.push({ ...group, fields: mandatoryFields });
      if (optionalFields.length > 0) optional.push({ ...group, fields: optionalFields });
    });
    return { mandatoryGroups: mandatory, optionalGroups: optional };
  }, [otherGroups]);

  // Track which accordion is open
  const [activeGroup, setActiveGroup] = useState<string>('mandatory');
  // Metadata (case type) card — controlled so a failed submit can re-open it if the user
  // collapsed it; it otherwise behaves as before (open on load, user-controlled after).
  const [isMetadataOpen, setIsMetadataOpen] = useState(true);

  // Reset active group when schema changes
  React.useEffect(() => {
    setActiveGroup('mandatory');
  }, [inputGroups]);

  // Pre-fill form when linked case record data arrives:
  // Only copy reporter_phone from the linked case (overwriting any existing value),
  // preserving all other fields the user has already filled in.
  useEffect(() => {
    const existingData = linkedCaseRecord?.input_data?.data;
    if (existingData && inputGroups.length > 0) {
      setFormData(prev => ({
        ...prev,
        reporter_phone: existingData.reporter_phone || prev.reporter_phone,
      }));
      setFieldErrors({});
      setLinkedCaseRecordId(null);
      setLinkingCallId(null);
      toast.success('Case linked successfully');
    }
  }, [linkedCaseRecord, inputGroups]);

  // Show toast when linked case record fetch fails (e.g. belongs to a different program)
  useEffect(() => {
    if (linkedCaseRecordError) {
      toast.error("This call doesn't belong to this program");
      setLinkedCaseRecordId(null);
      setSubmittableLinkedCaseId(null);
      setLinkingCallId(null);
    }
  }, [linkedCaseRecordError]);

  // Initialize form data when schema changes
  useEffect(() => {
    const initialData: DynamicFormData = {};
    inputGroups.forEach(group => {
      group.fields.forEach(field => {
        if (field.type === 'radio' && !field.defaultValue && field.options?.length) {
          initialData[field.id] = field.options[0].value;
        } else {
          initialData[field.id] = getDefaultValueForField(field);
        }
      });
    });
    setFormData(initialData);
  }, [inputGroups]);

  // Apply PMS autofill values (policy AUTOFILL, exactly one value — tech
  // design §6). Merge-into-prev and only into empty fields: agent-typed
  // values are never overwritten (R1). Reporter-block values join when the
  // who-is-calling answer lands (the plan recomputes on role change).
  useEffect(() => {
    const entries = Object.entries(pms.plan.autofill);
    if (!pmsEnabled || entries.length === 0) return;
    setFormData(prev => {
      const next = { ...prev };
      const filled: Record<string, string> = {};
      const fieldById = new Map(inputGroups.flatMap(g => g.fields).map(f => [f.id, f]));
      for (const [fieldId, value] of entries) {
        const current = prev[fieldId];
        if (current !== undefined && current !== null && current !== '') continue;
        // Enum fields commit only a value the control can represent (§6.6
        // applied to selects/radios: "F" matches option "female"; no match →
        // stay empty rather than silently hold an unrenderable value).
        const field = fieldById.get(fieldId);
        let commit: string | number | boolean | undefined = value;
        if (field?.options?.length) {
          commit = matchEnumOption(value, field.options);
        }
        if (commit === undefined) continue;
        next[fieldId] = commit;
        // The committed (possibly enum-matched) value is the restore source.
        filled[fieldId] = String(commit);
      }
      if (Object.keys(filled).length === 0) return prev;
      // Escape the updater before touching other state (markAutofilled is
      // idempotent, so a StrictMode double-invoke is harmless).
      queueMicrotask(() => pms.markAutofilled(filled));
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pms.plan.autofill, pmsEnabled]);

  // When the linked case changes, clear File-type fields that already hold an asset id: that asset
  // was uploaded to the *previously* linked case and must not leak onto the new one. Pending File
  // values (not yet uploaded, e.g. picked during manual creation) are kept — they upload to the
  // final linked case on submit.
  const prevLinkedCaseIdRef = useRef<string | null>(null);
  useEffect(() => {
    const prevLinkedCaseId = prevLinkedCaseIdRef.current;
    prevLinkedCaseIdRef.current = submittableLinkedCaseId;
    if (!prevLinkedCaseId || prevLinkedCaseId === submittableLinkedCaseId) return;

    const fileFieldIds = inputGroups
      .flatMap(group => group.fields)
      .filter(field => field.type === 'file')
      .map(field => field.id);
    if (fileFieldIds.length === 0) return;

    setFormData(prev => {
      let changed = false;
      const next = { ...prev };
      for (const id of fileFieldIds) {
        if (typeof next[id] === 'string' && next[id]) {
          next[id] = '';
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [submittableLinkedCaseId, inputGroups]);

  const handleInputChange = (fieldId: string, value: any) => {
    setFormData(prev => ({ ...prev, [fieldId]: value }));
  };

  const reporterRoleField = useMemo(
    () =>
      inputGroups
        .flatMap(g => g.fields)
        .find(
          f => f.id === 'reporter_role' || f.id === 'reporter_type' || f.id === 'who_is_calling',
        ),
    [inputGroups],
  );

  // Gate answer → (a) re-point the reporter block (R9: clear un-edited
  // PMS-autofilled reporter values so the new role's values re-apply), and
  // (b) set the schema's own reporter-role field — the gate and the form
  // field are the same question.
  const handleReporterRoleChange = useCallback(
    (role: PmsReporterRole) => {
      const cleared = pms.changeReporterRole(role);
      const roleField = inputGroups
        .flatMap(g => g.fields)
        .find(
          f => f.id === 'reporter_role' || f.id === 'reporter_type' || f.id === 'who_is_calling',
        );
      setFormData(prev => {
        const next = { ...prev };
        cleared.forEach(id => {
          next[id] = '';
        });
        if (roleField?.options?.length) {
          const opt = matchEnumOption(role, roleField.options);
          if (opt !== undefined) next[roleField.id] = opt;
        }
        return next;
      });
    },
    [pms, reporterRoleField],
  );

  // "Change patient" / start over: the hook returns every field id whose
  // value came from PMS (autofilled or accepted) and we blank those in the
  // form — otherwise the previous patient's data silently survives into the
  // next patient's case (the autofill effect only ever fills EMPTY fields,
  // so a new prefill could never overwrite them). Edited values stay: the
  // agent typed over them and owns them.
  const handlePmsReset = useCallback((): string[] => {
    const cleared = pms.reset();
    if (cleared.length > 0) {
      setFormData(prev => {
        const next = { ...prev };
        cleared.forEach(id => {
          next[id] = '';
        });
        return next;
      });
    }
    return cleared;
  }, [pms]);

  // PMS offer chips ride the existing suggestion plumbing (decision D5).
  // Accepted/ignored offers stop rendering; the field stays typeable
  // throughout. Also carries the provenance/gap badge (PMS / EDITED / ASK /
  // NOT IN PMS) and the EDITED state's restore affordance.
  const getPmsFieldSuggestionProps = useCallback(
    (fieldId: string, _fieldType: string) => {
      const offer = pmsEnabled ? pms.plan.offers[fieldId] : undefined;
      const state = pms.fieldStates[fieldId];
      const active = offer && state !== 'accepted' && state !== 'ignored';
      const current = formData[fieldId];
      const badge = pmsEnabled
        ? deriveFieldBadge(
            state,
            pms.plan.gaps[fieldId],
            current !== undefined && current !== null && current !== '',
          )
        : undefined;
      return {
        suggestion: active ? offer.suggestion : undefined,
        hint: undefined,
        isCollapsed: false,
        onAccept: () => pms.markAccepted(fieldId, offer?.suggestion ?? ''),
        onReject: () => pms.markIgnored(fieldId),
        onExpand: () => undefined,
        options: active ? offer.options : undefined,
        onAcceptOption: (value: string) => pms.markAccepted(fieldId, value),
        badge,
        onRestore:
          badge?.kind === 'edited'
            ? () => {
                const original = pms.restoreField(fieldId);
                // Write back through the raw setter — handleFieldChange would
                // immediately re-mark the field as edited.
                if (original !== undefined) handleInputChange(fieldId, original);
              }
            : undefined,
      };
    },

    [pms, pmsEnabled, formData],
  );

  const handleFieldChange = (fieldId: string, value: any) => {
    handleInputChange(fieldId, value);

    // A user-changed PMS value is no longer PMS-verified (drops from the
    // pms_sourced_fields tally). No-op for fields the PMS never touched.
    if (pmsEnabled) {
      pms.markFieldEdited(fieldId);

      // Bi-directional sync: editing the form's own reporter-role field IS
      // answering the who-is-calling question. Classify the option and
      // re-point the reporter block, same as answering the gate. (The
      // gate→field direction writes formData directly, not through this
      // handler, so the two directions cannot loop.)
      if (reporterRoleField && fieldId === reporterRoleField.id && typeof value === 'string') {
        const role = classifyReporterRole(value);
        if (role && role !== pms.reporterRole) {
          const cleared = pms.changeReporterRole(role);
          if (cleared.length > 0) {
            setFormData(prev => {
              const next = { ...prev };
              cleared.forEach(id => {
                next[id] = '';
              });
              return next;
            });
          }
        }
      }
    }

    // Mirror fields based on schema-driven mirrorWhenField/mirrorWhenValue conditions
    // Use newFormData to avoid stale closure — reflects the just-applied field change
    const newFormData = { ...formData, [fieldId]: value };
    const mirrorUpdates = getMirrorUpdates(inputGroups, newFormData, fieldId, value);
    Object.entries(mirrorUpdates).forEach(([targetId, mirrorValue]) => {
      handleInputChange(targetId, mirrorValue);
    });

    if (fieldErrors[fieldId]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldId];
        return newErrors;
      });
    }
  };

  const handleInputBlur = (fieldId: string) => {
    setFormData(prev => {
      const currentValue = prev[fieldId];
      if (typeof currentValue === 'string') {
        return { ...prev, [fieldId]: currentValue.trim() };
      }
      return prev;
    });
  };

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const handleOutputFieldChange = (fieldId: string, value: any) => {
    setOutputFormData(prev => ({ ...prev, [fieldId]: value }));
    if (fieldErrors[fieldId]) {
      setFieldErrors(prev => {
        const next = { ...prev };
        delete next[fieldId];
        return next;
      });
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (metadataGroup) {
      metadataGroup.fields.forEach(field => {
        const error = validateField(field, formData[field.id]);
        if (error) errors[field.id] = error;
      });
    }

    otherGroups.forEach(group => {
      group.fields.forEach(field => {
        const error = validateField(field, formData[field.id]);
        if (error) errors[field.id] = error;
      });
    });

    // Validate capture-at-create output fields against their own state (e.g. required ack_id).
    captureAtCreateGroups.forEach(group => {
      group.fields.forEach(field => {
        const error = validateField(field, outputFormData[field.id]);
        if (error) errors[field.id] = error;
      });
    });

    setFieldErrors(errors);

    if (Object.keys(errors).length === 0) {
      return true;
    }

    // Walk the sections in the order they appear on screen, so "first error" means the topmost
    // one. Scanning the schema's own group order instead would let an optional field win over a
    // mandatory one rendered above it, and send the user to the wrong accordion.
    const sections: Array<{ open: () => void; fieldIds: string[] }> = [
      {
        open: () => setIsMetadataOpen(true),
        // RadioCardGroup renders only non-hidden radios, so those are the only metadata fields
        // that can actually be shown and focused.
        fieldIds:
          metadataGroup?.fields.filter(f => f.type === 'radio' && !f.hidden).map(f => f.id) ?? [],
      },
      {
        open: () => setActiveGroup('mandatory'),
        // Capture-at-create fields render after the mandatory groups, against outputFormData.
        fieldIds: [
          ...renderedFieldIds(mandatoryGroups, formData),
          ...renderedFieldIds(captureAtCreateGroups, outputFormData),
        ],
      },
      {
        open: () => setActiveGroup('optional'),
        fieldIds: renderedFieldIds(optionalGroups, formData),
      },
    ];

    for (const section of sections) {
      const firstErrorFieldId = section.fieldIds.find(id => errors[id]);
      if (!firstErrorFieldId) continue;
      // Expand the section holding it, then focus the field once that render lands.
      section.open();
      focusFieldWhenRendered(firstErrorFieldId);
      break;
    }

    return false;
  };

  const handleAutoFill = () => {
    const savedData = autoFill();
    if (savedData) {
      setFormData(savedData);
      setFieldErrors({});
    }
  };

  // Link a call's case record and pre-fill the form with its data
  const handleLinkCall = async (call: CallInfo) => {
    const caseRecordId = call.caserecord_id;
    if (!caseRecordId) {
      toast.error('No case record associated with this call');
      return;
    }

    setLinkingCallId(call.id ?? null);
    // Only pre-fill form data — do NOT send the link-to-case signal here.
    // The signal is sent on submit (after updating input data) so the case
    // transitions from aepqc_preprocessing → pqc at the right time.
    setLinkedCaseRecordId(caseRecordId);
    setSubmittableLinkedCaseId(caseRecordId);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Backstop (LA-7428): if the linked case has already been created (e.g. it
    // was promoted in the gap between page load and submit), don't re-run
    // link-to-case — it would 400 with a generic error. Send the agent to the
    // existing case instead. The on-load guard already hides the form/Submit in
    // the common case; this covers the race.
    if (caseAlreadyCreated) {
      toast.error('This call already has a case. Opening it.');
      navigate(`../cases/${linkedCaseRecord?.id ?? submittableLinkedCaseId}`);
      return;
    }

    if (!selectedWorkflow) {
      toast.error('Please select a workflow first');
      return;
    }

    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    setIsSubmitting(true);

    try {
      if (!navigator.onLine) {
        toast.error('No internet connection. Please check your network and try again.');
        setIsSubmitting(false);
        return;
      }

      // Separate File-type field uploads from the JSON payload. In the link-to-case flow files are
      // uploaded immediately on selection (caseRecordId is set on the fields), so this is normally
      // empty; in manual create (live session) the picked Files are sent as multipart and uploaded
      // by the backend after the case is created. Stripping File instances also keeps them out of
      // the JSON/localStorage payloads (a File serializes to {}).
      const files: Record<string, File | File[]> = {};
      const jsonFormData: Record<string, any> = {};
      for (const [key, val] of Object.entries(formData)) {
        if (val instanceof File) {
          files[key] = val;
        } else if (Array.isArray(val) && val.some(v => v instanceof File)) {
          // Multi-file field value: split pending Files (sent as multipart) from already-uploaded
          // asset ids (kept in the JSON payload).
          const pending = val.filter((v): v is File => v instanceof File);
          const assetIds = val.filter(v => typeof v === 'string');
          files[key] = pending;
          if (assetIds.length > 0) {
            jsonFormData[key] = assetIds;
          }
        } else {
          jsonFormData[key] = val;
        }
      }
      const hasFiles = Object.keys(files).length > 0;

      // Reserved PMS keys (decision D4): provenance tally + case flags ride
      // input_data as out-of-schema keys — no case-record schema change.
      if (pmsEnabled) {
        Object.assign(jsonFormData, pms.submitKeys());
      }

      // Build output_data from capture-at-create fields (e.g. warm-transfer ack_id). Posts to
      // output_data, not input_data; the backend re-checks the capture_at_create flag before persisting.
      const jsonOutputData: Record<string, any> = {};
      captureAtCreateGroups.forEach(group =>
        group.fields.forEach(field => {
          const val = outputFormData[field.id];
          if (val !== undefined && val !== '') {
            jsonOutputData[field.id] = val;
          }
        }),
      );

      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Request timeout')), hasFiles ? 600000 : 35000);
      });

      // Link-to-case flow: update input data first (while case is still in
      // aepqc_preprocessing state with CanUpdateInput=true), then trigger
      // link-to-case signal which transitions the case to PQC.
      if (submittableLinkedCaseId) {
        // In linked mode the FileField uploads files as soon as the case is available (including
        // flushing a file deferred during manual creation when the call is linked), so by now every
        // file should already be an asset id. If a raw File is still present, its upload is still in
        // flight — ask the user to wait rather than silently dropping it.
        if (hasFiles) {
          toast.error('Please wait for the file upload to finish.');
          setIsSubmitting(false);
          return;
        }

        const updatePromise = updateCaseInput.mutateAsync({
          caseId: submittableLinkedCaseId,
          inputData: {
            schema_version: '1.0',
            data: jsonFormData,
          },
        });

        await Promise.race([updatePromise, timeoutPromise]);

        // Seed capture-at-create output fields (e.g. warm-transfer ack_id) while the case is still
        // in preprocessing. The backend permits capture_at_create keys through UpdateOutputData even
        // though CanUpdateOutput is false in this state, so this runs before the link-to-case signal.
        // Non-fatal (mirrors the standalone create flow): if it fails, warn but still send the link
        // signal so the case isn't left partially linked — the Ack ID can be added from the case page.
        if (hasCaptureAtCreateFields) {
          try {
            const outputPromise = updateCaseOutput.mutateAsync({
              caseId: submittableLinkedCaseId,
              outputData: {
                schema_version: '1.0',
                data: jsonOutputData,
              },
            });
            await Promise.race([outputPromise, timeoutPromise]);
          } catch {
            toast.error(
              'Case linked, but saving the Acknowledgment ID failed. Please add it from the case page.',
            );
          }
        }

        // Now send the link-to-case signal to transition aepqc_preprocessing → pqc
        const linkPromise = linkToCaseMutation.mutateAsync({
          caseRecordId: submittableLinkedCaseId,
          workflowId: selectedWorkflow?.id,
        });

        await Promise.race([linkPromise, timeoutPromise]);

        saveFormData(jsonFormData);
        handleCaseCreationSuccess(
          { case_record_id: submittableLinkedCaseId },
          { redirectToDetails: !!selectedWorkflow?.features?.skip_intake_draft },
        );
      } else {
        const createPromise = createCase.mutateAsync({
          workflowId: selectedWorkflow.id!,
          inputData: {
            schema_version: '1.0',
            data: jsonFormData,
          },
          files,
        });

        const result = (await Promise.race([createPromise, timeoutPromise])) as any;

        if (result && result.case_record_id) {
          // Seed capture-at-create output fields (e.g. warm-transfer ack_id) after the case exists.
          // UpdateOutputData permits capture_at_create keys even though CanUpdateOutput is false in
          // the freshly-created state. Non-fatal: the case is already created, so on failure we warn
          // and still proceed (the Ack ID can be added later from the case page).
          if (hasCaptureAtCreateFields) {
            try {
              await updateCaseOutput.mutateAsync({
                caseId: result.case_record_id,
                outputData: { schema_version: '1.0', data: jsonOutputData },
              });
            } catch {
              toast.error(
                'Case created, but saving the Acknowledgment ID failed. Please add it from the case page.',
              );
            }
          }
          saveFormData(jsonFormData);
          handleCaseCreationSuccess(result, {
            redirectToDetails: !!selectedWorkflow?.features?.skip_intake_draft,
          });
        }
      }
    } catch (error: any) {
      if (error?.message === 'Request timeout') {
        toast.error('Request timed out. Please check your connection and try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {/* Breadcrumb */}
      <BreadcrumbPortal>
        <Breadcrumb>
          <Breadcrumb.Item to="../cases">Cases</Breadcrumb.Item>
          <Breadcrumb.Item>Create</Breadcrumb.Item>
        </Breadcrumb>
      </BreadcrumbPortal>

      {/* Header Actions */}
      <HeaderActionsPortal>
        {isAutoFillEnabled && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={!hasSavedData || isSubmitting}
            className="flex items-center gap-2"
            onClick={handleAutoFill}
          >
            <RotateCcw className="w-4 h-4" />
            Auto Fill
          </Button>
        )}
        {!caseAlreadyCreated && (
          <Button
            type="submit"
            variant="default"
            size="sm"
            disabled={isSubmitting || !selectedWorkflow || workflowsLoading || schemaLoading}
            className="flex items-center gap-2"
            onClick={handleSubmit}
          >
            <SendHorizonal className="w-4 h-4" />
            {isSubmitting ? 'Creating...' : 'Submit'}
          </Button>
        )}
      </HeaderActionsPortal>

      {/* Two-column layout */}
      <div className="h-full flex overflow-hidden">
        {/* Left Panel - Create Case Form */}
        <div className="flex-1 overflow-y-auto bg-muted">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {workflowsLoading || schemaLoading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-foreground mx-auto mb-4"></div>
                <p className="text-muted-foreground">
                  {workflowsLoading ? 'Loading workflows...' : 'Loading form schema...'}
                </p>
              </div>
            ) : !selectedWorkflow ? (
              <div className="text-center py-12">
                <p className="text-muted-foreground">No workflows available for this program.</p>
              </div>
            ) : formSchema.groups.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-muted-foreground">No form schema available for this workflow.</p>
              </div>
            ) : caseAlreadyCreated ? (
              <div className="text-center py-16">
                <p className="text-lg font-medium text-foreground">
                  This call is already linked to a case
                </p>
                <p className="text-muted-foreground mt-1">
                  This call was already linked to a case
                  {linkedCaseRecord?.code ? ` (${linkedCaseRecord.code})` : ''}. You can create a
                  fresh case to start a new intake — the linked case won&apos;t be affected.
                </p>
                <div className="mt-5 flex flex-col items-center gap-2">
                  <Button
                    type="button"
                    variant="default"
                    size="sm"
                    className="mx-auto flex items-center gap-2"
                    onClick={handleCreateFreshCase}
                  >
                    Create a fresh case
                  </Button>
                  <Button
                    type="button"
                    variant="link"
                    size="sm"
                    className="mx-auto text-muted-foreground"
                    onClick={() =>
                      navigate(`../cases/${linkedCaseRecord?.id ?? submittableLinkedCaseId}`)
                    }
                  >
                    View linked case
                  </Button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Manufacturer — prominent top section identifying the workflow's brand */}
                <ManufacturerSection
                  name={selectedWorkflow?.name ?? ''}
                  brand={workflowBrand}
                  fallbackColorClass={workflowColorClass}
                  variant={selectedWorkflow?.variant}
                />

                {/* PMS patient lookup (AQ-580) — renders only for pms_enabled workflows */}
                {pmsEnabled && (
                  <>
                    <PatientLookupCard pms={{ ...pms, reset: handlePmsReset }} />
                    {pms.confirmedPatient && (
                      <WhoIsCallingGate
                        role={pms.reporterRole}
                        onRoleChange={handleReporterRoleChange}
                        awaitingCount={pms.plan.awaitingRole.length}
                      />
                    )}
                  </>
                )}

                {/* Metadata group rendered separately with RadioCardGroup */}
                {metadataGroup && (
                  <RadioCardGroup
                    group={metadataGroup}
                    formData={formData}
                    fieldErrors={fieldErrors}
                    disabled={isSubmitting}
                    onFieldChange={handleFieldChange}
                    open={isMetadataOpen}
                    onOpenChange={setIsMetadataOpen}
                  />
                )}

                {/* Fields split into Mandatory and Optional accordions */}
                <Accordion
                  type="single"
                  collapsible
                  value={activeGroup}
                  onValueChange={setActiveGroup}
                  className="space-y-4"
                >
                  {/* Mandatory Fields */}
                  <AccordionItem
                    value="mandatory"
                    className="bg-card rounded-xl border overflow-hidden"
                    disabled={isSubmitting}
                  >
                    <AccordionTrigger className="px-6 py-4 text-lg font-medium bg-muted/30 hover:bg-muted/60 transition-colors data-[state=open]:border-b">
                      Mandatory Fields
                    </AccordionTrigger>
                    <AccordionContent className="px-6 pb-6">
                      <div className="mt-4 space-y-8">
                        {mandatoryGroups.map(group => (
                          <div key={group.label}>
                            <h3 className="text-base font-semibold text-foreground mb-4">
                              {group.label}
                            </h3>
                            <FieldGroupRenderer
                              group={group}
                              formData={formData}
                              fieldErrors={fieldErrors}
                              disabled={isSubmitting}
                              onFieldChange={handleFieldChange}
                              onFieldBlur={handleInputBlur}
                              caseRecordId={submittableLinkedCaseId ?? undefined}
                              getFieldSuggestionProps={
                                pmsEnabled ? getPmsFieldSuggestionProps : undefined
                              }
                            />
                          </div>
                        ))}
                        {/* Capture-at-create output fields (schema-driven, e.g. warm-transfer
                            ack_id) are required, so they render within Mandatory Fields — before
                            Additional Fields — while still posting to output_data via outputFormData. */}
                        {captureAtCreateGroups.map(group => (
                          <div key={`cac-${group.label}`}>
                            <h3 className="text-base font-semibold text-foreground mb-4">
                              {group.label}
                            </h3>
                            <FieldGroupRenderer
                              group={group}
                              formData={outputFormData}
                              fieldErrors={fieldErrors}
                              disabled={isSubmitting}
                              onFieldChange={handleOutputFieldChange}
                            />
                          </div>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  {/* Additional Fields */}
                  <AccordionItem
                    value="optional"
                    className="bg-card rounded-xl border overflow-hidden"
                    disabled={isSubmitting}
                  >
                    <AccordionTrigger className="px-6 py-4 text-lg font-medium bg-muted/30 hover:bg-muted/60 transition-colors data-[state=open]:border-b">
                      Additional Fields
                    </AccordionTrigger>
                    <AccordionContent className="px-6 pb-6">
                      <div className="mt-4 space-y-8">
                        {optionalGroups.map(group => (
                          <div key={group.label}>
                            <h3 className="text-base font-semibold text-foreground mb-4">
                              {group.label}
                            </h3>
                            <FieldGroupRenderer
                              group={group}
                              formData={formData}
                              fieldErrors={fieldErrors}
                              disabled={isSubmitting}
                              onFieldChange={handleFieldChange}
                              onFieldBlur={handleInputBlur}
                              caseRecordId={submittableLinkedCaseId ?? undefined}
                              getFieldSuggestionProps={
                                pmsEnabled ? getPmsFieldSuggestionProps : undefined
                              }
                            />
                          </div>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </form>
            )}
          </div>
        </div>

        {/* Right Panel - Live Session Sidebar */}
        <LiveSessionPanel
          activeCall={activeCall}
          activeCallLoading={activeCallLoading}
          calls={callHistory}
          callsLoading={callHistoryLoading}
          linkingCallId={linkingCallId}
          onLinkCall={handleLinkCall}
          workflowName={
            shouldShowLinkConfirmation(getWorkflowsArray()) ? selectedWorkflow?.name : undefined
          }
        />
      </div>
    </>
  );
};
